import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { 
    TrendingUp,
    GraduationCap, Briefcase, Video, Baby, Wand2, Brain, Megaphone, ArrowRight
} from 'lucide-react';

type StepImage =
  | string
  | {
      webpSrcSet: string;
      jpgSrcSet: string;
      fallback: string;
      width: number;
      height: number;
    };

const stepImageSizes = '(min-width: 1024px) 50vw, 100vw';

const steps: Array<{
  id: number;
  num: string;
  title: string;
  description: string;
  image: StepImage;
}> = [
    {
      id: 1,
      num: '01',
      title: 'Create an account',
      description: 'Quick signup unlocks access to the full course library. Bypass lengthy onboarding and dive straight into the material in under two minutes.',
      image: {
        webpSrcSet: '/hands-laptop-keyboard-800.webp 800w, /hands-laptop-keyboard-1200.webp 1200w',
        jpgSrcSet: '/hands-laptop-keyboard-800.jpg 800w, /hands-laptop-keyboard-1200.jpg 1200w',
        fallback: '/hands-laptop-keyboard.jpg',
        width: 1200,
        height: 800,
      },
    },
    {
      id: 2,
      num: '02',
      title: 'Dive into courses',
      description: 'Choose your level and follow structured, beginner-to-pro tracks. Learn from industry experts through high-definition video lessons and interactive resources.',
      image: {
        webpSrcSet: '/library-800.webp 800w, /library-1200.webp 1200w',
        jpgSrcSet: '/library-800.jpg 800w, /library-1200.jpg 1200w',
        fallback: '/library.jpg',
        width: 1200,
        height: 686,
      },
    },
    {
      id: 3,
      num: '03',
      title: 'Download anytime',
      description: 'Access your lessons anywhere, even without a Wi-Fi connection. Download materials directly to your device for seamless offline review during your commute.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80'
    }
  ];

const AILearningHome = () => {
    const [activeStep, setActiveStep] = useState(1);
  const actionColorScheme = 'bg-zinc-950 border border-zinc-950 text-white hover:bg-white hover:text-zinc-950 hover:border-zinc-950';

    return (
        <>
            <Header />

        <main className="font-sans text-zinc-900 ">

                {/* ========================================
                    1. HERO SECTION (Business Scaling Focus)
                    ======================================== */}
 <section className="relative py-12 md:py-12 overflow-hidden border-b border-zinc-200">
  <div
    className="absolute inset-0 opacity-20"
    style={{
      backgroundImage:
        "linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)",
      backgroundSize: "40px 40px",
    }}
    ></div>

  <div className="absolute inset-0 "></div>

  <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#f59e0b]/10 rounded-full blur-[120px] pointer-events-none z-0"></div>

  <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center relative z-20 sm:w-fit ">
    {/* LEFT: Scaling & Skill Acquisition */}
    <div className="relative z-30 md:max-w-[105%] md:w-[650px] sm:w-fit">
      

      <h1 className="sm:w-auto text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 text-zinc-950 tracking-tighter md:max-w-[105%] md:w-[650px]">
        Acquire The Skills
        <span className="text-transparent bg-clip-text bg-[#f59e0b]">
          &nbsp; To Scale With AI.
        </span>
      </h1>

      

      <div className="flex flex-col sm:flex-row gap-4 mt-10">
        <Link
          to="/library"
          className={`${actionColorScheme} px-8 py-4 rounded-xl font-bold text-lg text-center transition-all shadow-xl shadow-zinc-200`}
        >
          Browse All Courses
        </Link>
           
      </div>
    </div>

    {/* RIGHT: Simple Picture */}
    <div className="relative w-full  h-[550px] md:h-[440px] lg:h-[550px] flex items-center justify-center lg:justify-end h-fit">
      {/* Here is the simple picture wrapper. 
         Aspect-square keeps it neat, max-w limits width on large screens.
         object-cover ensures the image fills the area.
      */}
      <div className="w-full h-fit max-w-[500px] md:max-w-[420px] lg:max-w-[500px] aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-[#f59e0b] border-4 border-white z-10">
        <img
          src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=800&auto=format&fit=crop"
          alt="Business team collaborating on AI scaling strategy"
          width="800"
          height="800"
          fetchPriority="high"
          loading="eager"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  </div>
</section>
             



{/* ========================================
    2. HOW IT WORKS: The Scaling Protocol (Light Mode - High Contrast Hover)
    ======================================== */}
 {/* ========================================
                    2. HOW IT WORKS: The Scaling Protocol (Restored Sticky Layout)
                    ======================================== */}
                <section className="bg-zinc-950 py-24 md:py-32 relative">
                    <div className="absolute top-0 right-1/4 w-[600px] h-[300px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none"></div>

                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 flex flex-col lg:flex-row gap-16 items-start">
                        
                        {/* LEFT COLUMN: Sticky */}
                        <div className="w-full lg:w-1/3 lg:sticky lg:top-8">
                            <div className="pt-4">
                               
                                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter leading-tight">
                                    Your 3-Step <br className="hidden lg:block"/> Scaling Plan
                                </h2>
                                <p className="text-zinc-300 text-lg leading-relaxed mb-8 max-w-sm">
                                    We move you from fundamental concepts to deploying complex business automations.
                                </p>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Steps Cards */}
                        <div className="w-full lg:w-2/3 flex flex-col gap-8">
                            {/* Step 1 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-[#f59e0b] transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-zinc-800/10 group-hover:text-[#f59e0b29] transition-colors leading-none">01</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <GraduationCap className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Acquire Core Skills</h3>
                                    <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Master <strong>AI Fundamentals for Professionals</strong>. Understand the underlying logic of GPT and automated intelligence.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>

                            {/* Step 2 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-[#f59e0b] transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-[f59e0b29] group-hover:text-[#f59e0b29] transition-colors leading-none">02</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <Briefcase className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Strategic Execution</h3>
                                    <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Apply <strong>AI Operations Management</strong> and <strong>Market Analysis</strong> to find profit-holes in your business.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>

                            {/* Step 3 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-zinc-800/10 group-hover:text-[#f59e0b29] transition-colors leading-none">03</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <TrendingUp className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Scale Your Authority</h3>
                                    <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Use <strong>ChatGPT for Branding</strong> to scale your personal presence on social media and dominate your niche.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                    3. ABOUT US: The Mission
                    ======================================== */}
                <section className="py-24 bg-white overflow-hidden border-t border-zinc-100">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
                        <div className="order-2 lg:order-1">
                            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">The Business Mission</span>
                            <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-6 leading-tight tracking-tighter">
                                Bridging the Gap Between <br/> Complexity & Profit.
                            </h2>
                            <p className="text-lg text-zinc-600 mb-6 font-medium leading-relaxed">
                                NeuralAcademy was created for entrepreneurs who don't have time for theory. Every course in our Courses is a direct skill acquisition module meant for immediate business application.
                            </p>
                           
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -top-6 -right-6 w-64 h-64" style={{ backgroundImage: 'radial-gradient(#d4d4d8 2px, transparent 2px)', backgroundSize: '20px 20px' }}></div>
                            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Leadership" width="1000" height="667" className="relative z-10 rounded-3xl shadow-2xl shadow-[#f59e0b] w-full h-[450px] md:h-[360px] lg:h-[450px] object-cover border-4 border-zinc-100" />
                        </div>
                    </div>
                </section>


                {/* ========================================
                    3. Our AI Courses
                    ======================================== */}
                <section className="py-24 bg-zinc-950 overflow-hidden">
                  <div className="max-w-7xl mx-auto px-6 lg:px-12">

                    <div className="mb-16 md:mb-24 text-center">
                        <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">Our AI Courses</span>
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight tracking-tighter">
                            <span className="text-transparent bg-clip-text bg-[#f59e0b]">Explore</span> What You'll Learn
                        </h2>
                        <p className="w-full max-w-3xl mx-auto text-lg text-zinc-300 mb-6 font-medium leading-relaxed">
                            Practical, beginner-friendly courses designed to help you use AI for content creation, marketing and everyday productivity.
                        </p>                        
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-6 md:mb-6 mb-4">
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Video className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:min-h-[64px] lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">Viral Quiz Videos for TikTok & Shorts</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Create quiz videos using Gemini, ElevenLabs and AI image tools.
                        </p>
                        <Link to="/library" aria-label="Open Viral Quiz Videos course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Baby className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:min-h-[64px] lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">Children’s Story Videos with AI</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Create stories, visuals, characters and voiceovers with AI.
                        </p>
                        <Link to="/library" aria-label="Open Children's Story Videos course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Wand2 className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:min-h-[64px] lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">Fantasy Niche Videos with AI</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Produce fantasy-themed videos using AI-generated scripts and visuals.
                        </p>
                        <Link to="/library" aria-label="Open Fantasy Niche Videos course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 mb-12">
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Brain className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">AI for Productivity</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Use AI to improve research, writing, planning and daily workflows.
                        </p>
                        <Link to="/library" aria-label="Open AI for Productivity course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Megaphone className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">AI in Digital Marketing</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Learn how AI can support content, campaigns and audience research.
                        </p>
                        <Link to="/library" aria-label="Open AI in Digital Marketing course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                    </div>

                    {/* five-icon-grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                      <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="20" width="20" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="fi_2783301"><g id="Layer_2" data-name="Layer 2">
                            <path d="M27.37,2.65H4.63A3.64,3.64,0,0,0,1,6.28V20.8a3.64,3.64,0,0,0,3.63,3.63H15v1.88l-3,0a1,1,0,0,0-1,1,1,1,0,0,0,1,1l8-.09a1,1,0,1,0,0-2l-3,0V24.43H27.37A3.64,3.64,0,0,0,31,20.8V6.28A3.64,3.64,0,0,0,27.37,2.65ZM29,20.8a1.63,1.63,0,0,1-1.63,1.63H4.63A1.63,1.63,0,0,1,3,20.8V6.28A1.63,1.63,0,0,1,4.63,4.65H27.37A1.63,1.63,0,0,1,29,6.28Z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">FORMAT</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Online, self-paced lessons
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg id="fi_2889676"className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="16" width="16" viewBox="0 0 35 35" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><g>
                            <path d="m17 1c-5 0-9 4-9 9v4c-1.7 0-3 1.3-3 3v13c0 1.7 1.3 3 3 3h18c1.7 0 3-1.3 3-3v-13c0-1.7-1.3-3-3-3v-4c0-5-4-9-9-9zm10 16v13c0 .6-.4 1-1 1h-18c-.6 0-1-.4-1-1v-13c0-.6.4-1 1-1h1 16 1c.6 0 1 .4 1 1zm-17-3v-4c0-3.9 3.1-7 7-7s7 3.1 7 7v4z"></path>
                            <path d="m17 19c-1.7 0-3 1.3-3 3 0 1.3.8 2.4 2 2.8v2.2c0 .6.4 1 1 1s1-.4 1-1v-2.2c1.2-.4 2-1.5 2-2.8 0-1.7-1.3-3-3-3zm0 4c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">ACCESS</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Available through your account
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 lg:ml-8 ml-0">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="20" width="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="fi_9308008"><g fill="rgb(0,0,0)">
                            <path d="m12 12.75c-3.17 0-5.75-2.58-5.75-5.75s2.58-5.75 5.75-5.75 5.75 2.58 5.75 5.75-2.58 5.75-5.75 5.75zm0-10c-2.34 0-4.25 1.91-4.25 4.25s1.91 4.25 4.25 4.25 4.25-1.91 4.25-4.25-1.91-4.25-4.25-4.25z"></path>
                            <path d="m20.5901 22.75c-.41 0-.75-.34-.75-.75 0-3.45-3.5199-6.25-7.8399-6.25-4.32005 0-7.84004 2.8-7.84004 6.25 0 .41-.34.75-.75.75s-.75-.34-.75-.75c0-4.27 4.18999-7.75 9.34004-7.75 5.15 0 9.3399 3.48 9.3399 7.75 0 .41-.34.75-.75.75z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">SKILL LEVEL</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Beginner-friendly
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="20" width="20" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="fi_3119243"><g id="Education">
                            <path d="M24,24a1.0067,1.0067,0,0,1-.356-.0654l-21-8a1,1,0,0,1,0-1.8692l21-8a1.0049,1.0049,0,0,1,.712,0l21,8a1,1,0,0,1,0,1.8692l-21,8A1.0067,1.0067,0,0,1,24,24ZM5.8091,15,24,21.93,42.1909,15,24,8.07Z"></path>
                            <path d="M24,38a1.0067,1.0067,0,0,1-.356-.0654l-13-4.95A1,1,0,0,1,10,32.05v-14a1,1,0,0,1,2,0V31.36l12,4.57,12-4.57V18.05a1,1,0,0,1,2,0v14a1,1,0,0,1-.644.9346l-13,4.95A1.0067,1.0067,0,0,1,24,38Z"></path>
                            <path d="M7,36a1,1,0,0,1-1-1V16.5239a1,1,0,0,1,.9106-.9961l17-1.5239a1,1,0,0,1,.1788,1.9922L8,17.4385V35A1,1,0,0,1,7,36Z"></path>
                            <path d="M9,42H5a1,1,0,0,1-1-1V37a3,3,0,0,1,6,0v4A1,1,0,0,1,9,42ZM6,40H8V37a1,1,0,0,0-2,0Z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">LEARNING STYLE</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Practical, step-by-step training
                          </p>
                        </div>
                      </div>
                      {/* <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg id="fi_7486301" className="lucide lucide-trending-up w-4 h-4 text-zinc-950" enable-background="new 0 0 60 60" height="20" viewBox="0 0 64 64" width="20" xmlns="http://www.w3.org/2000/svg"><path d="m32.99847 25.67719a5.396 5.396 0 0 1 2.2956 2.89281c.02258.08953.06756.26544-.028.084a1.43231 1.43231 0 0 1 .14929.62524c.06562.33973.99433-.32617 1.16639-.50384l3.47143-3.47144c2.86-2.05932-.20141-6.17968-2.8836-3.798l-3.92847 3.92865z"></path><path d="m42.01276 35.298-1.08246 1.09177a2.35591 2.35591 0 0 0 3.33148 3.33136l3.94714-3.93787a2.38 2.38 0 0 0 -1.689-4.04992 2.42438 2.42438 0 0 0 -1.67035.7279z"></path><path d="m37.35632 30.6416a2.31359 2.31359 0 0 0 -.69055 1.66095 2.38486 2.38486 0 0 0 4.03131 1.67029l2.85541-2.85535c.028-.01867.04663-.04663.07465-.0653l1.01709-1.01719a2.36233 2.36233 0 0 0 -3.35932-3.322z"></path><path d="m34.79016 21.24475a2.35908 2.35908 0 0 0 -3.36865-3.3034l-2.94885 2.93939c1.0653 1.13586 2.02728 2.5263 3.07946 3.61133.03315.01458 3.08081-3.09207 3.23804-3.24732z"></path><path d="m14.1394 25.52789a6.03126 6.03126 0 0 0 2.24891-.4386 14.23268 14.23268 0 0 1 2.19293-2.47283c1.89575-1.89271 4.44415-4.356 7.036-3.67658l-1.26911-1.26916a5.85057 5.85057 0 0 0 -.52252-7.67054l-4.29261-4.29254a5.84621 5.84621 0 0 0 -8.25842 0 .94865.94865 0 0 0 0 1.32507l4.18054 4.17121a3.00428 3.00428 0 1 1 -4.13385 4.35791l-4.2832-4.2926a.97715.97715 0 0 0 -1.32508 0 5.84617 5.84617 0 0 0 0 8.25848l4.29248 4.29248a5.82042 5.82042 0 0 0 4.13393 1.7077z"></path><path d="m26.91431 40.46771a.933.933 0 0 1 -1.40906-1.22253c3.56464-4.10578 1.44641-7.04518-.59725-9.87268a1.0041 1.0041 0 0 1 -.04663-1.40906c.7558-.75586 1.39038-.12134 2.92071 1.39038a29.83815 29.83815 0 0 0 2.32355 2.16492 2.20574 2.20574 0 0 0 3.38737-1.40912c.49341-1.7807-1.437-2.75372-2.59418-3.66718-1.77569-1.50714-3.14941-4.00616-4.94561-5.347-2.37018-1.77265-6.45423 3.1383-8.00666 5.02948-2.21789 3.45306-1.81555 4.62671-2.30475 7.81073-1.39013 6.35168-4.21051 8.70788-8.2586 12.73767l10.36747 10.36721 4.27387-4.22718a5.88573 5.88573 0 0 1 3.78863-1.62365c4.12451-.34528 5.515-.87714 9.20093-2.82752a8.52893 8.52893 0 0 0 2.29565-1.74487l-8.68775-8.69714a11.35107 11.35107 0 0 1 -1.70769 2.54754z"></path><path d="m58.287 44.46161-4.29248-4.29248a5.83212 5.83212 0 0 0 -6.20552-1.32513c-.13244.122-2.40246 2.43213-2.4635 2.43561a4.21958 4.21958 0 0 1 -6.65338-4.76849 4.21838 4.21838 0 0 1 -3.81665-4.84312 4.35828 4.35828 0 0 1 -1.73566 1.67975 3.99284 3.99284 0 0 1 -4.05926-.25208 6.692 6.692 0 0 1 .1773 2.8089l9.50885 9.50885.90522.90515a5.85848 5.85848 0 0 0 .52252 7.6706l4.29256 4.29251a5.81455 5.81455 0 0 0 4.12457 1.7077c1.208.15027 5.44776-1.31482 4.134-3.03278l-4.29266-4.2832a3.00358 3.00358 0 1 1 4.35791-4.13379l4.17109 4.18039a.977.977 0 0 0 1.32508 0 5.8461 5.8461 0 0 0 .00001-8.25839z"></path>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">TOOLS COVERED</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Gemini, ElevenLabs, Leonardo AI, Ideogram AI & other AI platforms
                          </p>
                        </div>
                      </div> */}
                    </div>

                    <div className="flex justify-center flex-col sm:flex-row gap-4 mt-10">
                      <Link
                        to="/library"
                        className={`bg-white border border-zinc-200 px-8 py-4 rounded-xl font-bold text-lg text-center transition-all hover:bg-transparent hover:text-white`}
                      >
                        Enroll Now
                      </Link>                        
                    </div>

                  </div>
                </section>


                {/* ========================================
                    Get started
                    ======================================== */}

<section className="bg-white py-24 md:py-32 border-y border-zinc-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        
        {/* Top Header */}
        <div className="mb-16 md:mb-24 text-center ">
          <div className="mx-auto inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">
            Get started
          </div>
       
        </div>

        {/* Main Interactive Section */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          
          {/* Left Side: Dynamic Image Gallery */}
          <div className="w-full lg:w-1/2 relative h-[400px] md:h-[420px] lg:h-[650px] rounded-[2.5rem] overflow-hidden bg-zinc-100 shadow-2xl shadow-[#f59e0b]">
            {steps.map((step) => {
              const isActive = activeStep === step.id;
              const imageClasses = `absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${
                isActive
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-105 pointer-events-none'
              }`;

              if (typeof step.image === 'string') {
                return (
                  <img
                    key={step.id}
                    src={step.image}
                    alt={step.title}
                    width="1200"
                    height="800"
                    loading={step.id === 1 ? 'eager' : 'lazy'}
                    className={imageClasses}
                  />
                );
              }

              return (
                <picture key={step.id} className="absolute inset-0">
                  <source type="image/webp" srcSet={step.image.webpSrcSet} sizes={stepImageSizes} />
                  <source type="image/jpeg" srcSet={step.image.jpgSrcSet} sizes={stepImageSizes} />
                  <img
                    src={step.image.fallback}
                    alt={step.title}
                    width={step.image.width}
                    height={step.image.height}
                    loading={step.id === 1 ? 'eager' : 'lazy'}
                    className={imageClasses}
                  />
                </picture>
              );
            })}
            {/* Subtle inner shadow overlay */}
            <div className="absolute inset-0 border border-black/5 rounded-[2.5rem] z-10 pointer-events-none"></div>
          </div>

          {/* Right Side: Expandable Accordion List */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            {steps.map((step) => {
              const isActive = activeStep === step.id;

              return (
                <button
                  type="button"
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`group w-full cursor-pointer border-b border-zinc-200 py-8 text-left transition-all duration-300 ${
                    isActive ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                  }`}
                  aria-expanded={isActive}
                  aria-controls={`home-step-${step.id}`}
                >
                  {/* Step Header (Always Visible) */}
                  <div className="flex items-center gap-6">
                    <span className={`text-4xl md:text-5xl font-black transition-colors duration-300 ${
                      isActive ? 'text-zinc-900' : 'text-zinc-700 group-hover:text-zinc-800'
                    }`}>
                      {step.num}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight flex-1">
                      {step.title}
                    </h3>
                    
                    {/* Animated Arrow Icon */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 text-lg shadow-xl shadow-zinc-200 ${actionColorScheme} ${isActive ? '' : 'opacity-80'}`}
                    >
                      <ArrowRight className={`w-5 h-5`} />
                    </div>
                  </div>

                  {/* Expandable Description (Uses CSS Grid for smooth height transition) */}
                    <div
                      id={`home-step-${step.id}`}
                    className={`grid transition-all duration-500 ease-in-out ${
                      isActive ? 'grid-rows-[1fr] opacity-100 mt-6' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden pl-14 md:pl-20 pr-4">
                      <p className="text-zinc-700 text-lg leading-relaxed font-medium">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </section>  

  
            </main>
            <Footer />
        </>
    );
};

export default AILearningHome;
